import { yearMonthDayPatternDashSeparator as yearFirstDatePattern } from '@oneview/constants/datePattern';
import { createQueryFn } from '@oneview/redux/utils';
import {
    BondQuote,
    BondTimeRange,
    formatDate,
    formatOptionChain,
    formatQuote,
    formatQuoteStat,
    getBondStartMonth,
    getEtfToCompare,
    OptionChain,
    parseOptionSymbol,
    Quote,
    QuoteHistory,
    QuoteStat,
    TimeRange,
} from '@orbis-systems/orbis-api-middleware';
import { chunk, isEmpty, omit, uniqBy } from 'lodash';
import { api } from './baseApi';
import { Api } from './configureApi';

const formatMidasQuote = (midasQuote: Quote): Quote => {
    const quote: Quote = {
        ...omit(midasQuote, ['tradeTime']),
        tradeTime: midasQuote.updateTime,
    };

    return formatQuote(quote);
};

const getMidasQuote =
    (api: Api) =>
    async (symbol: string, crypto?: boolean): Promise<Quote | null> => {
        const quotes: Quote[] = (
            await (crypto
                ? api.orbisSdk.get<Quote[]>('quotes/crypto', { params: { symbols: symbol } })
                : api.aesmidas.quotesReformat.getEquity({ symbols: symbol }))
        ).data;
        const quote = quotes.find((quote) => quote.symbol === symbol);
        return quote ? formatMidasQuote(quote) : null;
    };

const getMidasOptionQuote =
    (api: Api) =>
    async (symbol: string): Promise<Quote | null> => {
        const response = await api.aesmidas.quotesReformat.getOption({ symbols: symbol, greeks: true });
        const quote = response.data[0];
        if (isEmpty(quote) || quote?.notFound) {
            throw Error('Quote not found');
        }
        return quote;
    };

const getMidasBondQuote =
    (api: Api) =>
    async (id: string): Promise<BondQuote | null> => {
        const orbisQuote = await api.aesmidas.quotesReformat.getBonds({ identifiers: id });
        let quote = orbisQuote.data[0];

        if (isEmpty(quote) || quote?.notFound) {
            throw Error('Bond quote not found');
        }

        // FIXME: Insight fixed income info currently missing in midas
        const insightFixedIncomeInfo = await api.middlewareSdk.get('insight/v1/fixed-income/:id', undefined, {
            pathParams: { id },
        });

        quote = { ...quote, ...insightFixedIncomeInfo.data };

        // Description is a better value for companyName. We call for companyName from Quote in various components so this makes it more consistent
        const formattedBondQuote = {
            companyName: quote.issuer || quote.companyName,
        };

        return { ...quote, ...formattedBondQuote };
    };

const getMidasQuoteStat =
    (api: Api) =>
    async (symbol: string, range: TimeRange, crypto?: boolean): Promise<QuoteStat> => {
        const etfSymbol = getEtfToCompare(symbol);
        const [data, etfData] = await Promise.all(
            crypto && range === TimeRange.OneDay
                ? // FIX ME: Change to /crypto when back end fixes
                  [api.aesmidas.quotesReformat.getEquityIntraday({ symbol: symbol })]
                : crypto
                  ? [
                        api.orbisSdk.get('quotes/crypto/historical', undefined, {
                            params: { symbol, range },
                        }),
                    ]
                  : range === TimeRange.OneDay
                    ? [
                          api.aesmidas.quotesReformat.getEquityIntraday({ symbol: symbol }),
                          api.aesmidas.quotesReformat.getEquityIntraday({ symbol: etfSymbol }),
                      ]
                    : [
                          api.aesmidas.quotesReformat.getEquityHistorical({ symbol, range }),
                          api.aesmidas.quotesReformat.getEquityHistorical({ symbol: etfSymbol, range }),
                      ],
        );

        const [currentQuote, currentQuoteEtf] = await Promise.all([
            getMidasQuote(api)(symbol, crypto),
            getMidasQuote(api)(etfSymbol, crypto),
        ]);

        const combinedData = currentQuote ? [...data.data, currentQuote] : [data.data];
        const combinedDataEtf = currentQuoteEtf ? [...(etfData?.data || []), currentQuoteEtf] : [etfData?.data || []];

        return formatQuoteStat(combinedData, crypto ? [] : combinedDataEtf);
    };

const getMidasQuoteOptionStat =
    (api: Api) =>
    async (symbol: string, range: TimeRange): Promise<QuoteStat> => {
        const underlyingSymbol = parseOptionSymbol(symbol)?.symbol || '';
        const [data, underlyingSymbolData] = await Promise.all([
            api.orbisSdk.get('quotes/option/historical', undefined, {
                params: { symbol, range },
            }),
            api.orbisSdk.get('quotes/equity/historical', undefined, {
                params: { symbol: underlyingSymbol, range },
            }),
        ]);

        const [currentOptionQuote, currentQuote] = await Promise.all([
            getMidasOptionQuote(api)(symbol),
            getMidasQuote(api)(underlyingSymbol),
        ]);

        const combinedData = currentOptionQuote
            ? [...data.data, { ...currentOptionQuote, price: currentOptionQuote.lastPrice }]
            : [data.data];
        const combinedComparisonData = currentQuote
            ? [...(underlyingSymbolData.data || []), currentQuote]
            : [underlyingSymbolData.data || []];
        return formatQuoteStat(combinedData, combinedComparisonData);
    };

export const quoteMidasApi = api.injectEndpoints({
    endpoints: (build) => ({
        getMidasQuote: build.query<Quote | null, { symbol: string; crypto?: boolean }>({
            queryFn: createQueryFn(async ({ symbol, crypto = false }, { extra: api }) => {
                return getMidasQuote(api)(symbol, crypto);
            }),
        }),
        getMidasBondQuote: build.query<BondQuote | null, string>({
            queryFn: createQueryFn((id: string, { extra: api }) => {
                return getMidasBondQuote(api)(id);
            }),
        }),
        searchMidasQuotes: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        criteria,
                        timestamp,
                        limit,
                        disableBonds,
                        disableEquities,
                        disableMutualFunds,
                        onlyMutualFunds,
                    }: {
                        criteria: string;
                        timestamp: number;
                        limit?: number;
                        disableBonds?: boolean;
                        disableEquities?: boolean;
                        disableMutualFunds?: boolean;
                        onlyMutualFunds?: boolean;
                    },
                    { extra: api },
                ) => {
                    const equityQuotes: Quote[] = disableEquities
                        ? []
                        : await api.aesmidas.quotesReformat
                              .getSearch({
                                  criteria: criteria.toUpperCase(),
                                  limit: limit || 10,
                                  loadQuotes: true,
                              })
                              .then((data) => data.data?.map(formatMidasQuote));
                    const bondQuotes: Quote[] = disableBonds
                        ? []
                        : await api.aesmidas.quotesReformat
                              .getBondsSearch({
                                  criteria: criteria.toUpperCase(),
                                  limit: limit || 10,
                                  loadQuotes: true,
                              })
                              .then((data) => data.data?.map(formatMidasQuote));
                    const quotes = onlyMutualFunds
                        ? equityQuotes.filter((quote) => quote.mutualFund)
                        : disableMutualFunds
                          ? equityQuotes.filter((quote) => !quote.mutualFund)
                          : equityQuotes;

                    const data = [...quotes, ...bondQuotes].filter((quote) => !quote.notFound);
                    return {
                        timestamp: timestamp,
                        results: data,
                    };
                },
            ),
        }),
        getMidasQuotes: build.query({
            queryFn: createQueryFn(
                async ({ symbols, crypto = false }: { symbols: string[]; crypto?: boolean }, { extra: api }) => {
                    const maxQuotes = 50;
                    const responses = await Promise.all(
                        chunk(symbols, maxQuotes).map((symbolList) =>
                            crypto
                                ? api.orbisSdk.get<Quote[]>('quotes/crypto', {
                                      params: { symbols: symbolList.join(',') },
                                  })
                                : api.aesmidas.quotesReformat.getEquity({ symbols: symbolList.join(',') }),
                        ),
                    );
                    const quotes: Quote[] = responses.flatMap((response) => response.data);
                    // Equity endpoint still returns crypto data for some reason, so we filter here to make sure no duplicates
                    const filteredData = uniqBy(quotes, (item) => item.symbol);
                    return filteredData.map(formatQuote).filter((quote) => !quote.notFound);
                },
            ),
        }),
        getMidasQuoteHistorical: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        symbol,
                        range,
                        crypto,
                        option,
                        bond,
                    }: {
                        symbol: string;
                        range: TimeRange;
                        crypto?: boolean;
                        option?: boolean;
                        bond?: boolean;
                    },
                    { extra: api },
                ) => {
                    const bondStartDate = getBondStartMonth(range);
                    return bond
                        ? // FIXME: Bond historical currently missing in midas
                          api.orbisMiddleware.stat.getBondHistorical(
                              symbol,
                              range === TimeRange.OneDay ? BondTimeRange.FifteenMinute : BondTimeRange.OneDay,
                              bondStartDate,
                              formatDate(new Date(), { pattern: yearFirstDatePattern }),
                          )
                        : option
                          ? getMidasQuoteOptionStat(api)(symbol, range)
                          : getMidasQuoteStat(api)(symbol, range, crypto);
                },
            ),
        }),
        getMidasQuoteAnalystsChart: build.query<QuoteHistory[], string>({
            queryFn: createQueryFn(async (symbol, { extra: api }) => {
                const response = await api.aesmidas.quotesReformat.getEquityHistorical({
                    symbol,
                    range: TimeRange.SevenMonth,
                });
                return response.data;
            }),
        }),
        getMidasQuoteIntraday: build.query<QuoteHistory[], string>({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) => {
                const response = await api.aesmidas.quotesReformat.getEquityIntraday({ symbol });
                return response.data;
            }),
        }),
        getMidasOptionQuote: build.query({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) => getMidasOptionQuote(api)(symbol)),
        }),
        getMidasOptionChainQuotes: build.query<OptionChain, { symbol: string; date: string }>({
            queryFn: createQueryFn(async ({ symbol, date }: { symbol: string; date: string }, { extra: api }) => {
                const response = await api.aesmidas.quotesReformat.getOptionChain(symbol, date);
                return formatOptionChain(response.data);
            }),
        }),
        getMidasQuotesFxRate: build.query({
            queryFn: createQueryFn(async ({ symbols }: { symbols: string[] }, { extra: api }) => {
                const { data } = await api.aesmidas.quotesReformat.getQuotesReformat({
                    symbols: symbols.join(','),
                });

                return data;
            }),
        }),
        getMidasQuoteOptionChainDates: build.query({
            queryFn: createQueryFn(async (symbol: string, { extra: api }) => {
                const response = await api.aesmidas.quotesReformat.getOptionChainDates(symbol);

                return response.data;
            }),
        }),
    }),
});
